"use client";

import { useEffect, useMemo, useRef } from "react";
import { useCompletions } from "./useCompletions";
import { markGoalCompletedTx } from "@/lib/db/goals";
import { isFamilyGoal, type Goal } from "@/lib/schemas";

export type FamilyGoalProgress = {
  /** Liczba zaliczonych jednostek (zadań pomocy lub zadań offline). */
  progress: number;
  /** Mapa uid → liczba zadań tego usera w obrębie celu. */
  breakdown: Map<string, number>;
  loading: boolean;
};

/**
 * Postęp celu rodziny — liczony z approved completions w HH od `createdAt`
 * celu. Source of truth = `completions`, **bez** cache'owania w `goals/{gid}`.
 *
 * Dla `family_count`: liczy completion z `kind === "family"`.
 * Dla `family_offline_count`: liczy completion z `offline === true`.
 *
 * Auto-mark: gdy `progress >= targetCount` i `!completedAt`, side-effect
 * woła `markGoalCompletedTx` (idempotent, transakcyjny guard po stronie
 * Firestore). Side-effect mountowany jednokrotnie per goal id przez useRef.
 *
 * Side note: dla `individual_money` zwraca `progress=0, breakdown=()` —
 * tam progress liczony z `member.balanceMinor`, nie z completions.
 */
export function useGoalProgress(
  hid: string | undefined,
  goal: Goal | null,
): FamilyGoalProgress {
  // Listener na wszystkie approved completions w HH — potrzebny niezależnie
  // od kind goala, żeby breakdown widział kontrybucje wszystkich.
  const { completions, loading } = useCompletions(hid, { status: "approved" });

  const result = useMemo<FamilyGoalProgress>(() => {
    if (!goal || !isFamilyGoal(goal)) {
      return { progress: 0, breakdown: new Map(), loading: false };
    }
    const sinceMs = goal.createdAt.toMillis();
    const filterFn =
      goal.kind === "family_count"
        ? (c: (typeof completions)[number]) => c.kind === "family"
        : (c: (typeof completions)[number]) => c.offline === true;

    const breakdown = new Map<string, number>();
    let progress = 0;
    for (const c of completions) {
      const approvedMs = c.approvedAt?.toMillis() ?? c.completedAt.toMillis();
      if (approvedMs < sinceMs) continue;
      if (!filterFn(c)) continue;
      progress += 1;
      breakdown.set(c.completedBy, (breakdown.get(c.completedBy) ?? 0) + 1);
    }
    return { progress, breakdown, loading };
  }, [completions, goal, loading]);

  // Auto-mark completedAt — side effect przy spełnieniu progu.
  const triggeredRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!hid || !goal || !isFamilyGoal(goal)) return;
    if (goal.completedAt) return;
    if (result.progress < goal.targetCount) return;
    if (triggeredRef.current.has(goal.id)) return;
    triggeredRef.current.add(goal.id);
    void markGoalCompletedTx(hid, goal.id).catch(() => {
      triggeredRef.current.delete(goal.id);
    });
  }, [hid, goal, result.progress]);

  return result;
}
